import { postAssessments } from 'api/assessments';
import { Flex, Modal } from 'components';
import { IGenericModal } from 'components/Modal/Modal';
import { P } from 'components/Typography/Typography';
import { SwitchButton } from 'components/_form';
import { IOption } from 'components/_form/Select/Select';
import { IOption as OptionForSelect } from 'components/_form/NestedSelect/NestedSelect';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { CreateAssessmentBody } from 'types/assessments';
import { FormAssessment } from './FormAssessment/FormAssessment';
import { useAssessmentContext } from 'contexts/AssessmentContext';
import { iso8601HourAndHalf } from 'constants/iso8601';
import { useAccount } from 'hooks/useAccount';

export type FormData = Omit<
  CreateAssessmentBody,
  'assessment_participations_attributes'
>;

export type FormDataControl = FormData & {
  practice_id: IOption;
  version: IOption;
  assessment_type: IOption;
  assessment_participations_attributes: {
    value: string;
    label: string | JSX.Element;
    function_id?: string;
  }[];
};

interface Props extends IGenericModal {
  isOpen: boolean;
  onCancelClick: () => void;
}

export const CreateAssessmentModal: React.FC<Props> = ({
  isOpen,
  onCancelClick
}) => {
  const [approvalSwitch, setApprovalSwitch] = useState(false);
  const [selectedPractice, setSelectedPractice] = useState<OptionForSelect[]>(
    []
  );

  const { t } = useTranslation();
  const { selectedAssessment, handleLoadAssessments } = useAssessmentContext();

  const { loggedUserData } = useAccount();

  const methods = useForm<FormDataControl>();

  const { handleSubmit, reset } = methods;

  const defaultFormValues: FormData = useMemo(() => {
    return {
      realization_date: new Date().toJSON().slice(0, 10).toString(),
      assessment_type: {
        value: 'normal',
        label: 'normal'
      },
      version: {
        value: '3.0-GW',
        label: '3.0-GW'
      },
      date: new Date().toJSON().slice(0, 10).toString(),
      name: '',
      description: '',
      group: '',
      practice_id:
        selectedAssessment !== undefined ? selectedAssessment.practice?.id : 1,
      area_id: selectedAssessment?.area.id?.toString() || '',
      related_to_assessment_id: '',
      planned_duration: iso8601HourAndHalf,
      duration: iso8601HourAndHalf,
      type: 'Assessment',
      notes: [],
      leader_id: undefined,
      team_leader_id: undefined,
      team_manager_id: undefined,
      archived_at: '',
      planned_start_date: '',
      planned_completion_date: '',
      only_realization: false,
      start_time: '',
      completion_time: '',
      assessment_participations_attributes: [],
      team_ids: [],
      closing_ids: [loggedUserData.id]
    };
  }, [loggedUserData.id, selectedAssessment]);

  useEffect(() => {
    reset(defaultFormValues);
  }, [defaultFormValues, reset]);

  useEffect(() => {
    if (selectedAssessment)
      setSelectedPractice([
        {
          label: selectedAssessment?.practice.name,
          value: selectedAssessment?.practice.id.toString(10)
        }
      ]);
  }, [selectedAssessment]);

  const handleSubmitAssessmentForm = useCallback(
    async (data: FormDataControl, closeOnSubmit = true) => {
      try {
        const payload = {
          assessment: {
            ...data,
            type: 'Assessment',
            practice_id: Number(selectedPractice[0].value),
            assessment_type: data.assessment_type.value,
            author_id: JSON.parse(localStorage.getItem('currentUser') as string)
              .id,
            assessment_participations_attributes:
              data.assessment_participations_attributes?.map(
                (assessmentParticipationAttribute) => ({
                  participant_id: assessmentParticipationAttribute.value,
                  function_id: assessmentParticipationAttribute.function_id
                })
              )
          }
        };

        const postAssessmentsRes = await postAssessments(payload);
        await handleLoadAssessments();
        toast.success(t('common.addedAssessment'));

        if (closeOnSubmit) {
          onCancelClick();
          reset(defaultFormValues);
        }

        if (postAssessmentsRes.data) {
          return postAssessmentsRes.data;
        }
      } catch (error) {
        console.log(error);
        toast.error(t('common.anErrorOccurred'));
      }
    },
    [handleLoadAssessments, onCancelClick, selectedPractice, t]
  );

  return (
    <Modal
      isOpen={isOpen}
      onCancelClick={onCancelClick}
      mainButton={{
        action: () => {
          handleSubmit((data) => handleSubmitAssessmentForm(data))();
        },
        variant: 'eucalyptus',
        label: t(`assessmentsView.assessmentSubmitButton`)
      }}
      isSecondButtonVisible={true}
      gridTemplateColumns="95%"
      bodyMaxHeight="80vh"
      headerTitle={t(`assessmentsView.createAssessment`)}
      additionalFooter={
        <Flex
          minWidth="250px"
          alignItems="center"
          gap="10px"
          display={['none', 'flex']}
        >
          <P variant="body">{t('createTaskView.footer')}</P>
          <SwitchButton value={approvalSwitch} onChange={setApprovalSwitch} />
        </Flex>
      }
    >
      <FormProvider {...methods}>
        <FormAssessment
          selectedPractice={selectedPractice}
          setSelectedPractice={setSelectedPractice}
          handleSubmitAssessmentForm={handleSubmitAssessmentForm}
        />
      </FormProvider>
    </Modal>
  );
};
